import React, { useState, useEffect, useRef, useContext } from 'react';

import LoadingImg from '../images/loading.gif';
import * as Constants from '../Constants';
import IdInput from '../components/idInput';
import CameraCapture from '../components/cameraCapture';
import IdLayout from '../components/_layout';
import { ShareContext } from '../context/sharecontext';

/**
 * Real, logged-in Face Match & Liveness verification page (FRD 5.1-5.5), wired behind the
 * same auth as the rest of the app (ShareContext token/profile, Authorization header) and
 * hitting the real api/Verification/... endpoints (billed, multi-tenant), unlike
 * pilotverify.jsx which hits the no-auth PublicVerificationController pilot mirror.
 *
 * No bank selection, by design: a "bank" here IS a Company, and a logged-in user only ever
 * belongs to one Company. There is no shared platform-wide list to pick from — a company gets
 * access to this feature at all only once a platform Admin/superadmin has enabled it for that
 * company specifically (CompanyVerificationSetting.IsEnabled), and it's billed at its own unit
 * price (VerificationPricing), separate from the NIN lookup's own unit cost. So the moment a
 * user logs in, CompanyId comes straight from their profile — nothing to choose. This page is
 * simply not usable by an account with no CompanyId (e.g. a platform Admin/superadmin, who
 * isn't a bank operator and has no company context to run a verification under).
 */
function VerifyPage() {
    const { profile, token } = useContext(ShareContext);
    const companyId = profile?.companyId || 0;

    const [identifierType] = useState('NIN');
    const [identifier, setIdentifier] = useState('');

    const [step, setStep] = useState('nin'); // nin -> facematch -> liveness -> result
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');

    const [attemptId, setAttemptId] = useState(null);
    const [faceMatchMaxRetries, setFaceMatchMaxRetries] = useState(0);
    const [referencePhoto, setReferencePhoto] = useState(null);
    const [applicantDetails, setApplicantDetails] = useState(null);

    const [faceMatchResult, setFaceMatchResult] = useState(null);

    const [challenge, setChallenge] = useState(null); // {actions, attemptsRemaining}
    const [capturing, setCapturing] = useState(false);
    const [livenessResult, setLivenessResult] = useState(null);
    // Which of challenge.actions the applicant is currently being asked to perform (0, 1, 2) — the
    // liveness check is now done one action at a time rather than all 3 in one capture, per explicit
    // product decision ("let system ask one by one... automatically change to the last one").
    const [currentActionIndex, setCurrentActionIndex] = useState(0);
    // Actions confirmed so far in the current challenge round, mirrored from the backend's
    // confirmedSoFar (server-side is still the source of truth — see VerificationController.LivenessVerify).
    const [confirmedActions, setConfirmedActions] = useState([]);

    const [finalResult, setFinalResult] = useState(null);

    // The selfie captured for Face Match, kept only so the progress-bar "Face Match" info panel can
    // show what was actually compared — not sent anywhere new, purely for on-screen review.
    const [capturedSelfie, setCapturedSelfie] = useState(null);
    // Which step's "saved info" panel is currently open (see the progress bar below) — null = closed.
    const [openStepInfo, setOpenStepInfo] = useState(null);

    const cameraRef = useRef(null);

    // The 4 stages shown in the progress bar. Order matches the step state machine
    // ('nin' -> 'facematch' -> 'liveness' -> 'result').
    const STEPS = [
        { key: 'nin', label: 'NIN Lookup' },
        { key: 'facematch', label: 'Face Match' },
        { key: 'liveness', label: 'Liveness' },
        { key: 'result', label: 'Result' },
    ];
    const stepIndex = (key) => STEPS.findIndex((s) => s.key === key);
    const currentStepIdx = stepIndex(step);
    const stepStatus = (key) => {
        const idx = stepIndex(key);
        if (idx < currentStepIdx) return 'done';
        if (idx === currentStepIdx) return 'current';
        return 'upcoming';
    };
    // A step is clickable once there's something saved for it to show — never for a step that
    // hasn't happened yet.
    const stepHasInfo = (key) => {
        if (key === 'nin') return !!attemptId;
        if (key === 'facematch') return !!faceMatchResult;
        if (key === 'liveness') return confirmedActions.length > 0 || !!livenessResult;
        if (key === 'result') return !!finalResult;
        return false;
    };

    useEffect(() => {
        Constants.seo({ title: 'Verify', metaDescription: '' });
    }, []);

    const callApi = async (path, method, body) => {
        const resp = await fetch(`${Constants.rooturl}/Verification${path}`, {
            method,
            body: body ? JSON.stringify(body) : undefined,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': token,
            },
        });
        return resp.json();
    };

    const resetAll = () => {
        setStep('nin');
        setIdentifier('');
        setErr('');
        setAttemptId(null);
        setFaceMatchMaxRetries(0);
        setReferencePhoto(null);
        setApplicantDetails(null);
        setFaceMatchResult(null);
        setChallenge(null);
        setLivenessResult(null);
        setCurrentActionIndex(0);
        setConfirmedActions([]);
        setFinalResult(null);
        setCapturedSelfie(null);
        setOpenStepInfo(null);
    };

    // ---- Step 1: identifier lookup ----
    const onStart = async (e) => {
        e.preventDefault();
        if (!companyId) {
            setErr('Your account is not linked to a bank.');
            return;
        }
        setLoading(true);
        setErr('');
        try {
            const data = await callApi('/start', 'POST', {
                companyId,
                identifierType,
                identifier,
            });
            setLoading(false);
            if (data.code === 0) {
                setAttemptId(data.attemptId);
                setFaceMatchMaxRetries(data.faceMatchMaxRetries);
                setReferencePhoto(data.referencePhoto);
                setApplicantDetails(data.applicantDetails || null);
                setStep('facematch');
            } else if (data.code === -3) {
                setErr('Insufficient unit balance to run a verification. Please fund the wallet.');
            } else if (data.code === -4) {
                setErr('This bank does not have the verification feature enabled.');
            } else if (data.code === -10) {
                setErr('Your session has expired. Please log in again.');
            } else {
                setErr(data.status || 'Identity lookup failed.');
            }
        } catch (e) {
            setLoading(false);
            setErr(Constants.serverErr);
        }
    };

    // ---- Step 2: Face Match ----
    const onCaptureFaceMatch = async () => {
        const frame = cameraRef.current?.captureFrame();
        if (!frame) {
            setErr('Camera is not ready yet.');
            return;
        }
        setCapturedSelfie(frame);
        setLoading(true);
        setErr('');
        try {
            const data = await callApi(`/${attemptId}/facematch`, 'POST', { capturedImageBase64: frame });
            setLoading(false);
            if (data.code === 0) {
                setFaceMatchResult(data);
                if (data.match) {
                    setTimeout(() => setStep('liveness'), 1200);
                } else if (data.locked) {
                    loadFinalResult();
                }
            } else {
                setErr(data.status || 'Face match failed.');
                if (data.locked) loadFinalResult();
            }
        } catch (e) {
            setLoading(false);
            setErr(Constants.serverErr);
        }
    };

    // ---- Step 3: Liveness ----
    useEffect(() => {
        if (step === 'liveness' && !challenge) {
            loadChallenge();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    const loadChallenge = async () => {
        setLoading(true);
        setErr('');
        try {
            const data = await callApi(`/${attemptId}/liveness/challenge`, 'GET');
            setLoading(false);
            if (data.code === 0) {
                setChallenge(data);
                setLivenessResult(null);
                setCurrentActionIndex(0);
                setConfirmedActions([]);
            } else {
                setErr(data.status || 'Could not start the liveness check.');
                if (data.locked) loadFinalResult();
            }
        } catch (e) {
            setLoading(false);
            setErr(Constants.serverErr);
        }
    };

    const instructionFor = (action) => {
        if (action === 'blink') return 'Blink naturally';
        if (action === 'turnleft') return 'Slowly turn your head to your LEFT';
        if (action === 'turnright') return 'Slowly turn your head to your RIGHT';
        return action;
    };

    // Short labels for the per-action progress dots / info panels (vs. the full instruction sentence above).
    const instructionShortFor = (action) => {
        if (action === 'blink') return 'Blink';
        if (action === 'turnleft') return 'Turn Left';
        if (action === 'turnright') return 'Turn Right';
        return action;
    };

    // One action at a time: captures a short burst while the applicant performs ONLY
    // challenge.actions[currentActionIndex], verifies just that action, and — on a pass — either
    // auto-advances to the next action or (once all 3 are confirmed) moves straight to the result.
    // On a fail, currentActionIndex is left unchanged so the same action can be retried.
    const onCaptureLiveness = async () => {
        if (!challenge) return;
        const currentAction = challenge.actions[currentActionIndex];
        if (!currentAction) return;
        setCapturing(true);
        setErr('');
        try {
            const FRAME_COUNT = 15;
            const FRAME_INTERVAL_MS = 220;
            const frames = await cameraRef.current?.captureBurst(FRAME_COUNT, FRAME_INTERVAL_MS);
            setCapturing(false);

            if (!frames || frames.length === 0) {
                setErr('Could not capture frames from the camera.');
                return;
            }

            setLoading(true);
            const data = await callApi(`/${attemptId}/liveness/verify`, 'POST', {
                requestedActions: [currentAction],
                framesBase64: frames,
            });
            setLoading(false);

            if (data.code === 0) {
                setLivenessResult(data);
                if (data.passed) {
                    setConfirmedActions(data.confirmedSoFar || []);
                    if (data.allConfirmed) {
                        setTimeout(() => loadFinalResult(), 1000);
                    } else {
                        // Show the "passed" confirmation briefly, then automatically move on to the
                        // next action — this is the "when one pass it show the second and
                        // automatically change to the last one" behavior.
                        setTimeout(() => {
                            setCurrentActionIndex((idx) => idx + 1);
                            setLivenessResult(null);
                        }, 1200);
                    }
                } else if (data.locked) {
                    loadFinalResult();
                }
                // A plain fail (not locked) leaves currentActionIndex where it is, so the button
                // below re-prompts the SAME action for a retry.
            } else {
                setErr(data.status || 'Liveness check failed.');
                if (data.locked) loadFinalResult();
            }
        } catch (e) {
            setCapturing(false);
            setLoading(false);
            setErr(Constants.serverErr);
        }
    };

    const onRetryLiveness = () => {
        setChallenge(null);
        setLivenessResult(null);
        setCurrentActionIndex(0);
        setConfirmedActions([]);
        loadChallenge();
    };

    // ---- Step 4: Result ----
    const loadFinalResult = async () => {
        setLoading(true);
        try {
            const data = await callApi(`/${attemptId}/result`, 'GET');
            setLoading(false);
            if (data.code === 0) {
                setFinalResult(data.result);
                setStep('result');
            }
        } catch (e) {
            setLoading(false);
            setErr(Constants.serverErr);
        }
    };

    return (
        <IdLayout active={'verify'} profile={profile}>
            {/* Wider on the Face Match step specifically, so the NIN photo, live camera, and NIN
                Record Details panel can sit side by side instead of stacking full-width — reduces
                scrolling per explicit feedback. Every other step keeps the original narrower width. */}
            <div style={{ maxWidth: step === 'facematch' ? 760 : 480, margin: '24px auto', padding: 16 }}>
                <h2 style={{ textAlign: 'center' }}>Face Match &amp; Liveness</h2>

                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginBottom: 24 }}>
                    {STEPS.map((s, idx) => {
                        const status = stepStatus(s.key);
                        const clickable = stepHasInfo(s.key);
                        return (
                            <React.Fragment key={s.key}>
                                {idx > 0 && (
                                    <div style={{ width: 28, height: 2, marginTop: 14, background: status === 'upcoming' ? '#ddd' : '#2a8f2a' }} />
                                )}
                                <div
                                    onClick={() => clickable && setOpenStepInfo(s.key)}
                                    title={clickable ? 'Click to view saved info' : ''}
                                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: 74, cursor: clickable ? 'pointer' : 'default' }}
                                >
                                    <div style={{
                                        width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 'bold', fontSize: 13,
                                        background: status === 'done' ? '#2a8f2a' : status === 'current' ? '#eef7ee' : '#eee',
                                        color: status === 'done' ? 'white' : status === 'current' ? '#2a8f2a' : '#999',
                                        border: status === 'current' ? '2px solid #2a8f2a' : '1px solid #ccc',
                                    }}>
                                        {status === 'done' ? '✓' : idx + 1}
                                    </div>
                                    <div style={{ fontSize: 11, textAlign: 'center', color: status === 'upcoming' ? '#999' : '#333', textDecoration: clickable ? 'underline' : 'none' }}>
                                        {s.label}
                                    </div>
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>

                {loading && (
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                        <img src={LoadingImg} style={{ width: 120 }} alt="Loading" />
                    </div>
                )}

                {err && <div style={{ color: 'red', textAlign: 'center', fontWeight: 'bold', marginBottom: 16 }}>{err}</div>}

                {step === 'nin' && !companyId && (
                    <div style={{ textAlign: 'center', color: '#777' }}>
                        Your account is not linked to a bank, so this feature isn't available here.
                    </div>
                )}

                {step === 'nin' && companyId > 0 && (
                    <form onSubmit={onStart}>
                        <IdInput label="NIN" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required />
                        <div style={{ textAlign: 'center', marginTop: 16 }}>
                            <button className="greenbutton" type="submit">Start Verification</button>
                        </div>
                    </form>
                )}

                {step === 'facematch' && (
                    <div style={{ textAlign: 'center' }}>
                        <h3>Step 2: Face Match</h3>
                        <p style={{ fontSize: 13 }}>Position your face in frame and capture a selfie to compare against the NIN record photo.</p>

                        {/* NIN photo + live camera + NIN Record Details all in one row (wraps on
                            narrow viewports) — kept together so the details panel is visible right
                            beside the camera instead of requiring a scroll past it. */}
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 16, alignItems: 'flex-start' }}>
                            {referencePhoto && (
                                <div>
                                    <div style={{ fontSize: 12, marginBottom: 4 }}>NIN Record Photo</div>
                                    <img
                                        src={referencePhoto.startsWith('data:') ? referencePhoto : `data:image/jpeg;base64,${referencePhoto}`}
                                        style={{ width: 140, height: 140, objectFit: 'cover', borderRadius: 8, border: '1px solid #ccc' }}
                                        alt="Reference"
                                    />
                                </div>
                            )}
                            <div>
                                <div style={{ fontSize: 12, marginBottom: 4 }}>Live Camera</div>
                                <CameraCapture ref={cameraRef} />
                            </div>
                            {applicantDetails && (
                                <div style={{ textAlign: 'left', width: 260, fontSize: 13, background: '#f7f7f7', borderRadius: 8, padding: 12 }}>
                                    <div style={{ fontWeight: 'bold', marginBottom: 6 }}>NIN Record Details</div>
                                    {[
                                        ['Tracking ID', applicantDetails.trackingId],
                                        ['NIN', applicantDetails.idNumber],
                                        ['Surname', applicantDetails.lastName],
                                        ['First Name', applicantDetails.firstName],
                                        ['Middle Name', applicantDetails.middleName],
                                        ['Gender', applicantDetails.gender],
                                        ['Date of Birth', applicantDetails.dateOfBirth],
                                        ['Country of Birth', applicantDetails.birthCountry],
                                        ['Phone Number', applicantDetails.mobile],
                                        ['Email', applicantDetails.email],
                                        ['Residential', applicantDetails.address && [
                                            applicantDetails.address.addressLine,
                                            applicantDetails.address.town,
                                            applicantDetails.address.lga,
                                            applicantDetails.address.state,
                                        ].filter(Boolean).join(', ')],
                                    ]
                                        .filter(([, v]) => v !== null && v !== undefined && v !== '')
                                        .map(([label, value]) => (
                                            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '2px 0' }}>
                                                <span style={{ color: '#666' }}>{label}</span>
                                                <span style={{ fontWeight: 500, textAlign: 'right' }}>{value}</span>
                                            </div>
                                        ))}
                                </div>
                            )}
                        </div>

                        {faceMatchResult && (
                            <div style={{ marginBottom: 12, fontSize: 14 }}>
                                <div><strong>Match:</strong> {faceMatchResult.match ? 'YES' : 'NO'}</div>
                                <div><strong>Confidence:</strong> {faceMatchResult.confidenceLevel}</div>
                                <div><strong>Retries remaining:</strong> {faceMatchResult.retriesRemaining}</div>
                            </div>
                        )}

                        <button className="greenbutton" onClick={onCaptureFaceMatch} disabled={loading}>
                            {faceMatchResult && !faceMatchResult.match ? 'Try Again' : 'Capture & Verify'}
                        </button>
                    </div>
                )}

                {step === 'liveness' && (
                    <div style={{ textAlign: 'center' }}>
                        <h3>Step 3: Liveness Check</h3>

                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                            <CameraCapture ref={cameraRef} />
                        </div>

                        {/* One action at a time — this row shows where the applicant is in the 3-action
                            sequence, but only the CURRENT action's instruction is shown below it. */}
                        {challenge && (
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 14, marginBottom: 16 }}>
                                {challenge.actions.map((a, idx) => {
                                    const done = confirmedActions.includes(a);
                                    const isCurrent = idx === currentActionIndex && !done;
                                    return (
                                        <div key={a} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: done ? '#2a8f2a' : isCurrent ? '#333' : '#aaa' }}>
                                            <span style={{
                                                width: 20, height: 20, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                background: done ? '#2a8f2a' : isCurrent ? '#eef7ee' : '#eee',
                                                color: done ? 'white' : '#333',
                                                fontWeight: 'bold', fontSize: 11,
                                                border: isCurrent ? '2px solid #2a8f2a' : '1px solid #ccc',
                                            }}>
                                                {done ? '✓' : idx + 1}
                                            </span>
                                            {instructionShortFor(a)}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {challenge && !capturing && challenge.actions[currentActionIndex] && (
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ fontWeight: 'bold', fontSize: 16 }}>
                                    {instructionFor(challenge.actions[currentActionIndex])}
                                </div>
                                <div style={{ fontSize: 12, marginTop: 4 }}>
                                    Attempts remaining: {livenessResult ? livenessResult.retriesRemaining : challenge.attemptsRemaining}
                                </div>
                            </div>
                        )}

                        {capturing && <div style={{ marginBottom: 16, fontWeight: 'bold' }}>Recording... perform the action now</div>}

                        {livenessResult && (
                            <div style={{ marginBottom: 12, fontSize: 14 }}>
                                <div>
                                    <strong>{livenessResult.passed ? 'Detected' : 'Not detected'}:</strong>{' '}
                                    {livenessResult.action ? instructionShortFor(livenessResult.action) : ''}
                                </div>
                            </div>
                        )}

                        {challenge && challenge.actions[currentActionIndex] && (
                            <button className="greenbutton" onClick={onCaptureLiveness} disabled={loading || capturing}>
                                {livenessResult && !livenessResult.passed ? 'Try Again' : 'Capture'}
                            </button>
                        )}
                        {livenessResult && !livenessResult.passed && !capturing && (
                            <button style={{ marginLeft: 8 }} onClick={onRetryLiveness}>Restart Sequence</button>
                        )}
                    </div>
                )}

                {step === 'result' && finalResult && (
                    <div style={{ textAlign: 'center' }}>
                        <h3>Result</h3>
                        <table style={{ margin: '0 auto', fontSize: 14 }}>
                            <tbody>
                                <tr><td style={{ textAlign: 'right', paddingRight: 8 }}>Overall</td><td><strong>{finalResult.overallStatus}</strong></td></tr>
                                <tr><td style={{ textAlign: 'right', paddingRight: 8 }}>NIN Lookup</td><td>{finalResult.ninLookupStatus}</td></tr>
                                <tr><td style={{ textAlign: 'right', paddingRight: 8 }}>Face Match</td><td>{finalResult.faceMatchStatus} ({finalResult.faceMatchConfidence})</td></tr>
                                <tr>
                                    <td style={{ textAlign: 'right', paddingRight: 8, verticalAlign: 'top' }}>Liveness</td>
                                    <td>
                                        <div>{finalResult.livenessStatus}</div>
                                        {finalResult.livenessActionsRequested && finalResult.livenessActionsRequested.length > 0 && (
                                            <div style={{ marginTop: 4, fontSize: 12 }}>
                                                {finalResult.livenessActionsRequested.map((a) => {
                                                    const passed = (finalResult.livenessActionsConfirmed || []).includes(a);
                                                    return (
                                                        <div key={a} style={{ color: passed ? '#2a8f2a' : '#c0392b' }}>
                                                            {passed ? '✓' : '✗'} {instructionShortFor(a)}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                                {finalResult.failureReason && (
                                    <tr><td style={{ textAlign: 'right', paddingRight: 8 }}>Reason</td><td>{finalResult.failureReason}</td></tr>
                                )}
                            </tbody>
                        </table>
                        <div style={{ marginTop: 16 }}>
                            <button className="greenbutton" onClick={resetAll}>Start Over</button>
                        </div>
                    </div>
                )}
            </div>

            {openStepInfo && (
                <div
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                    onClick={() => setOpenStepInfo(null)}
                >
                    <div style={{ background: 'white', borderRadius: 8, padding: 20, maxWidth: 380, width: '90%', maxHeight: '80vh', overflowY: 'auto', textAlign: 'left' }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <h4 style={{ margin: 0 }}>{STEPS.find((s) => s.key === openStepInfo)?.label} — Saved Info</h4>
                            <button type="button" onClick={() => setOpenStepInfo(null)}>Close</button>
                        </div>

                        {openStepInfo === 'nin' && (
                            <div style={{ fontSize: 13 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                    <span style={{ color: '#666' }}>Identifier</span><span>{identifier}</span>
                                </div>
                                {applicantDetails ? (
                                    [
                                        ['Name', `${applicantDetails.firstName || ''} ${applicantDetails.lastName || ''}`.trim()],
                                        ['Gender', applicantDetails.gender],
                                        ['Date of Birth', applicantDetails.dateOfBirth],
                                        ['Phone', applicantDetails.mobile],
                                        ['Email', applicantDetails.email],
                                    ]
                                        .filter(([, v]) => v)
                                        .map(([label, value]) => (
                                            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                                <span style={{ color: '#666' }}>{label}</span><span>{value}</span>
                                            </div>
                                        ))
                                ) : (
                                    <div style={{ color: '#888', marginTop: 6 }}>No further details were returned for this identifier.</div>
                                )}
                            </div>
                        )}

                        {openStepInfo === 'facematch' && faceMatchResult && (
                            <div style={{ fontSize: 13 }}>
                                <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 10 }}>
                                    {referencePhoto && (
                                        <img
                                            src={referencePhoto.startsWith('data:') ? referencePhoto : `data:image/jpeg;base64,${referencePhoto}`}
                                            alt="Reference"
                                            style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }}
                                        />
                                    )}
                                    {capturedSelfie && (
                                        <img src={capturedSelfie} alt="Captured" style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }} />
                                    )}
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                    <span style={{ color: '#666' }}>Match</span><span>{faceMatchResult.match ? 'YES' : 'NO'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                    <span style={{ color: '#666' }}>Confidence</span><span>{faceMatchResult.confidenceLevel}</span>
                                </div>
                            </div>
                        )}

                        {openStepInfo === 'liveness' && (
                            <div style={{ fontSize: 13 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                    <span style={{ color: '#666' }}>Actions confirmed</span>
                                    <span>{confirmedActions.length ? confirmedActions.map(instructionShortFor).join(', ') : 'none yet'}</span>
                                </div>
                                {livenessResult && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                        <span style={{ color: '#666' }}>Last attempt</span>
                                        <span>{livenessResult.passed ? 'Passed' : 'Not detected'} ({livenessResult.action ? instructionShortFor(livenessResult.action) : ''})</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {openStepInfo === 'result' && finalResult && (
                            <div style={{ fontSize: 13 }}>
                                {[
                                    ['Overall', finalResult.overallStatus],
                                    ['NIN Lookup', finalResult.ninLookupStatus],
                                    ['Face Match', `${finalResult.faceMatchStatus} (${finalResult.faceMatchConfidence})`],
                                    ['Liveness', finalResult.livenessStatus],
                                    ['Reason', finalResult.failureReason],
                                ]
                                    .filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== 'undefined (undefined)')
                                    .map(([label, value]) => (
                                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                            <span style={{ color: '#666' }}>{label}</span><span>{value}</span>
                                        </div>
                                    ))}
                                {finalResult.livenessActionsRequested && finalResult.livenessActionsRequested.length > 0 && (
                                    <div style={{ marginTop: 6, borderTop: '1px solid #eee', paddingTop: 6 }}>
                                        {finalResult.livenessActionsRequested.map((a) => {
                                            const passed = (finalResult.livenessActionsConfirmed || []).includes(a);
                                            return (
                                                <div key={a} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                                                    <span style={{ color: '#666' }}>{instructionShortFor(a)}</span>
                                                    <span style={{ color: passed ? '#2a8f2a' : '#c0392b' }}>{passed ? 'Passed' : 'Failed'}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </IdLayout>
    );
}

export default VerifyPage;
